import React, { useCallback, useEffect } from "react";
import { useDebounceValue } from "usehooks-ts";
import { useTranslation } from "i18n";
import {
  Autocomplete,
  IAutocompleteItem,
} from "../../../../../Atoms/Autocomplete/Autocomplete";
import { IconTextLink } from "../../../../../Atoms/IconTextLink/IconTextLink";
import { openExternalLinkInNewTab } from "../../../../../../generics/navigation";
import { useGetPerformerSearch } from "../hooks/useGetPerformerSearch";

interface Props {
  searchTerm: string;
  searchRedirect: (searchTerm: string) => void;
}

const CdAgentSearchAutocomplete = ({
  searchTerm = "",
  searchRedirect,
}: Props) => {
  const { t } = useTranslation();
  const [debouncedSearch, setSearch] = useDebounceValue(searchTerm, 500);

  const { data = [], isLoading } = useGetPerformerSearch(debouncedSearch);

  const formatPerformerName = useCallback(
    (item: IAutocompleteItem) => {
      const uppercasedName = item.name.toUpperCase();
      const replacedValue = debouncedSearch?.toUpperCase();
      const label = uppercasedName.replace(
        replacedValue,
        `<b>${replacedValue}</b>`
      );

      return `${label} <br> <small>(${item.data.type})</small>`;
    },
    [debouncedSearch]
  );

  useEffect(() => {
    setSearch(searchTerm);
  }, [searchTerm, setSearch]);

  return (
    <Autocomplete
      display={!!debouncedSearch}
      list={data}
      selectItem={(item) => openExternalLinkInNewTab(item.data.url)}
      formatName={formatPerformerName}
      renderTail={() => (
        <div className="text-color-neutral-one px-5 py-4">
          {data.length === 0 && !isLoading && t("common:nav.search.noResults")}
          {isLoading && t("common:nav.search.label.loading")}
          {data.length > 0 && (
            <IconTextLink
              variant="secondary"
              text={t("common:nav.search.moreResults", {
                searchTerm: debouncedSearch,
              })}
              onClick={() => searchRedirect(searchTerm)}
            />
          )}
        </div>
      )}
      className="max-h-[calc(100vh_-_60px)]"
    />
  );
};

export default CdAgentSearchAutocomplete;
